import {
  Box,
  Card,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import { MoveRight, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelectFieldType } from '@/type';

interface MultiSelectInputProps {
  field: any;
  multiSelectField: MultiSelectFieldType;
  error?: any;
}

const MultiSelectInput = ({ field, multiSelectField, error }: MultiSelectInputProps) => {
  const { t } = useTranslation();

  const options = useMemo(
    () => multiSelectField.options || [],
    [multiSelectField.options],
  );

  const [filterLeft, setFilterLeft] = useState('');
  const [filterRight, setFilterRight] = useState('');

  const left = useMemo(
    () => options.filter((item) => !(field.value || []).includes(item.id)),
    [options, field.value],
  );
  const right = useMemo(
    () => options.filter((item) => (field.value || []).includes(item.id)),
    [options, field.value],
  );

  const filteredLeft = left.filter((item) =>
    item.label.toLowerCase().includes(filterLeft.toLowerCase()),
  );
  const filteredRight = right.filter((item) =>
    item.label.toLowerCase().includes(filterRight.toLowerCase()),
  );

  const handleAddItem = (id: any) => {
    const newSelected = [...(field.value || []), id];
    field.onChange(newSelected);
    multiSelectField.onChange?.(newSelected);
  };

  const handleRemoveItem = (id: any) => {
    const newSelected = (field.value || []).filter((itemId: any) => itemId !== id);
    field.onChange(newSelected);
    multiSelectField.onChange?.(newSelected);
  };

  const handleAddAll = () => {
    const newSelected = [...(field.value || []), ...filteredLeft.map((item) => item.id)];
    field.onChange(newSelected);
    multiSelectField.onChange?.(newSelected);
  };

  const handleRemoveAll = () => {
    const newSelected = (field.value || []).filter(
      (id: any) => !filteredRight.map((item) => item.id).includes(id),
    );
    field.onChange(newSelected);
    multiSelectField.onChange?.(newSelected);
  };

  const customLeftList = () => (
    <Card
      variant='outlined'
      sx={{ width: 200, height: 400, display: 'flex', flexDirection: 'column' }}
    >
      <CardHeader
        sx={{ px: 2, py: 1 }}
        title={''}
        subheader={`${filteredLeft.length} ${t('common.available')}`}
        action={
          <IconButton
            sx={{ width: '28px !important' }}
            size='small'
            onClick={handleAddAll}
            disabled={filteredLeft.length === 0}
          >
            <MoveRight size={18} />
          </IconButton>
        }
      />
      <Divider />
      <Box p={1}>
        <TextField
          value={filterLeft}
          onChange={(e) => setFilterLeft(e.target.value)}
          placeholder={t('common.search-placeholder')}
          size='small'
          fullWidth
        />
      </Box>
      <Divider />
      <List dense component='div' role='list' sx={{ overflow: 'auto', flexGrow: 1 }}>
        {filteredLeft.map((item) => (
          <ListItem
            key={item.id}
            role='listitem'
            button
            onClick={() => handleAddItem(item.id)}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Card>
  );

  const customRightList = () => (
    <Card
      variant='outlined'
      sx={{ width: 200, height: 400, display: 'flex', flexDirection: 'column' }}
    >
      <CardHeader
        sx={{ px: 2, py: 1 }}
        title={''}
        subheader={`${filteredRight.length} ${t('common.selected')}`}
        action={
          <IconButton
            sx={{ width: '28px !important' }}
            size='small'
            onClick={handleRemoveAll}
            disabled={filteredRight.length === 0}
          >
            <Trash2 size={16} />
          </IconButton>
        }
      />
      <Divider />
      <Box p={1}>
        <TextField
          value={filterRight}
          onChange={(e) => setFilterRight(e.target.value)}
          placeholder={t('common.search-placeholder')}
          size='small'
          fullWidth
        />
      </Box>
      <Divider />
      <List dense component='div' role='list' sx={{ overflow: 'auto', flexGrow: 1 }}>
        {filteredRight.map((item) => (
          <ListItem
            key={item.id}
            role='listitem'
            secondaryAction={
              <IconButton edge='end' onClick={() => handleRemoveItem(item.id)}>
                <Trash2 size={16} />
              </IconButton>
            }
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Card>
  );

  return (
    <FormControl fullWidth required={multiSelectField.required} error={!!error}>
      <InputLabel shrink>{t(multiSelectField.label)}</InputLabel>
      <Box mt={1}>
        <Grid
          container
          spacing={2}
          justifyContent='center'
          alignItems='flex-start'
          wrap='nowrap'
        >
          <Grid item>{customLeftList()}</Grid>
          <Grid item>{customRightList()}</Grid>
        </Grid>
      </Box>
      {error && <FormHelperText>{t(error.message || '')}</FormHelperText>}
    </FormControl>
  );
};

export default MultiSelectInput;
